<script setup lang="ts">
import type { Charge } from '@/services/order'
import type { ImageMap } from '@/services/productImage'
import { formatDate } from '@/lib/formatters/date'
import { formatMoneyWithCurrency } from '@/lib/formatters/money'

import Modal from '@/components/Modal.vue'
import Button from '@/components/Button.vue'
import BowerbirdLogo from '@/assets/images/bowerbird.svg?url'
import ProductImageBubbleList from '@/components/ProductImageBubbleList.vue'

const props = defineProps<{
  order: Charge
  imagesByProductId: Record<string, ImageMap[] | null | undefined>
}>()

const emit = defineEmits<{
  (e: 'done'): void
}>()

const getImagesForProduct = (id: string) => {
  const imageList = props.imagesByProductId[id]
  if (imageList) {
    return imageList[0]
  } else {
    return undefined
  }
}
</script>

<template>
  <Modal :has-close-button="false">
    <template #outer>
      <div class="NextOrderConfirmationModal__Logo">
        <img :src="BowerbirdLogo" />
      </div>
    </template>
    <div class="NextOrderConfirmationModal">
      <div class="NextOrderConfirmationModal__Content">
        <h3>We've got your order!</h3>
        <div class="NextOrderConfirmationModal__OrderMeta">
          Processes {{ formatDate('DD Mon', order.scheduled_at) }}
          <span>•</span>
          {{ order.line_items.reduce((acc, item) => acc + item.quantity, 0) }}
          items
          <span>•</span>
          {{ formatMoneyWithCurrency(order.total_price) }}
        </div>
        <p>
          We’re working on your order, and it should arrive within 3-5 days.
        </p>
        <div class="NextOrderConfirmationModal__ProductImages">
          <ProductImageBubbleList
            :images="
              order.line_items.map((li) => ({
                url: getImagesForProduct(
                  `${li.external_product_id.ecommerce}:${li.external_variant_id.ecommerce}`,
                )?.small,
                alt: li.title,
              }))
            "
          ></ProductImageBubbleList>
        </div>
        <div class="NextOrderConfirmationModal__Footer">
          <Button :block="true" @click="emit('done')">Done</Button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style>
.NextOrderConfirmationModal {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: stretch;
  gap: 15px;
  position: relative;
  top: -10px;
}

.NextOrderConfirmationModal__Logo {
  position: relative;
  top: 0;
  border-radius: 1000px;
  background-color: var(--color-white);
  height: calc(96px + 16px);
  width: calc(96px + 16px);
  border: 8px solid var(--color-white);
  margin: -40px auto 0;
  overflow: hidden;

  & svg {
    position: relative;
    z-index: 2;
  }
}

.NextOrderConfirmationModal__Header {
  position: relative;
  border-radius: 1000px;
  background-color: var(--color-white);
  height: calc(96px + 16px);
  width: calc(96px + 16px);
  border: 8px solid var(--color-white);
  margin-top: -40px;
  overflow: hidden;

  & svg {
    position: relative;
    z-index: 2;
  }
}

.NextOrderConfirmationModal__Content {
  text-align: center;
}

.NextOrderConfirmationModal__OrderMeta {
  font-weight: bold;

  & span {
    font-weight: normal;
    color: var(--color-forest-50);
    padding: 0 5px;
  }
}

.NextOrderConfirmationModal__ProductImages {
  margin-bottom: 20px;
}
</style>
